/**
 * Cloudflare Turnstile token helper
 *
 * Renders an invisible widget once on first call, then mints a fresh
 * single-use token via execute() on each subsequent call. Token is sent
 * as `turnstileToken` in the agent payload body and validated server-side.
 *
 * Site key is public (Cloudflare gates by domain whitelist), so embedding
 * a literal default is fine — VITE_TURNSTILE_SITE_KEY overrides for clones.
 */

const SITE_KEY: string =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ||
  '0x4AAAAAADHTTnnaiwaMPkKZ'

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      size?: 'normal' | 'compact' | 'invisible' | 'flexible'
      callback?: (token: string) => void
      'error-callback'?: (err?: unknown) => void
      'expired-callback'?: () => void
      execution?: 'render' | 'execute'
      retry?: 'auto' | 'never'
    },
  ) => string
  execute: (widgetIdOrContainer: string | HTMLElement) => void
  reset: (widgetIdOrContainer: string | HTMLElement) => void
  getResponse: (widgetIdOrContainer: string | HTMLElement) => string | undefined
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let widgetId: string | null = null
let container: HTMLDivElement | null = null
let pendingResolvers: Array<(token: string) => void> = []
let pendingRejecters: Array<(err: Error) => void> = []

function ensureContainer(): HTMLDivElement {
  if (container) return container
  const div = document.createElement('div')
  div.style.position = 'fixed'
  div.style.left = '-9999px'
  div.style.top = '-9999px'
  div.setAttribute('aria-hidden', 'true')
  document.body.appendChild(div)
  container = div
  return div
}

async function waitForTurnstile(timeoutMs = 8000): Promise<TurnstileApi> {
  const start = Date.now()
  while (!window.turnstile) {
    if (Date.now() - start > timeoutMs) {
      throw new Error('Turnstile script failed to load')
    }
    await new Promise((r) => setTimeout(r, 50))
  }
  return window.turnstile
}

function flushSuccess(token: string) {
  const resolvers = pendingResolvers
  pendingResolvers = []
  pendingRejecters = []
  for (const r of resolvers) r(token)
}

function flushFailure(err: Error) {
  const rejecters = pendingRejecters
  pendingResolvers = []
  pendingRejecters = []
  for (const r of rejecters) r(err)
}

async function ensureWidget(): Promise<void> {
  if (widgetId !== null) return
  const turnstile = await waitForTurnstile()
  widgetId = turnstile.render(ensureContainer(), {
    sitekey: SITE_KEY,
    size: 'invisible',
    execution: 'execute',
    retry: 'auto',
    callback: (token) => flushSuccess(token),
    'error-callback': (err) =>
      flushFailure(new Error(`Turnstile error: ${String(err ?? 'unknown')}`)),
    'expired-callback': () =>
      flushFailure(new Error('Turnstile token expired before use')),
  })
}

/**
 * Get a fresh single-use Turnstile token. Resolves with the token string
 * or rejects on error/timeout. Subsequent calls reuse the same widget.
 */
export async function getTurnstileToken(timeoutMs = 10_000): Promise<string> {
  await ensureWidget()
  const turnstile = window.turnstile!
  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingResolvers = pendingResolvers.filter((r) => r !== wrappedResolve)
      pendingRejecters = pendingRejecters.filter((r) => r !== wrappedReject)
      reject(new Error('Turnstile token timeout'))
    }, timeoutMs)
    const wrappedResolve = (t: string) => {
      clearTimeout(timer)
      resolve(t)
    }
    const wrappedReject = (e: Error) => {
      clearTimeout(timer)
      reject(e)
    }
    pendingResolvers.push(wrappedResolve)
    pendingRejecters.push(wrappedReject)
    try {
      turnstile.reset(widgetId!)
      turnstile.execute(widgetId!)
    } catch (e) {
      wrappedReject(e instanceof Error ? e : new Error(String(e)))
    }
  })
}

/**
 * Best-effort prefetch: render the widget early so the first real
 * getTurnstileToken() call doesn't pay the script-load latency.
 */
export function prewarmTurnstile(): void {
  ensureWidget().catch(() => {
    // Non-fatal — getTurnstileToken will retry on demand.
  })
}
